// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

export interface AppFromApi {
  id: string;
  key: string;
  name: string;
  description: string;
  icon: string;
  route: string;
  color: string;
  sortOrder: number;
  isEnabled: boolean;
  isVisible: boolean;
  metadata: {
    category: string;
    gradient: [string, string];
    badge?: string;
    isExternal?: boolean;
  };
  config?: Record<string, any>;
}

export interface AppStoreResponse {
  apps: AppFromApi[];
  categories: Record<string, { name: string; color: string }>;
}

/**
 * Fetch App Store apps from backend API
 * Falls back to defaults if API is unavailable
 */
export async function fetchAppStoreApps(token?: string): Promise<AppStoreResponse> {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/app-configurations/appstore/apps`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.warn('Failed to fetch from API, using defaults:', error);
    return getDefaultApps();
  }
}

/**
 * Get default apps configuration (fallback)
 */
export function getDefaultApps(): AppStoreResponse {
  return {
    apps: [
      {
        id: 'default_thiranai',
        key: 'thiranai',
        name: 'ThiranAI',
        description: 'AI-powered RAG platform with document management and intelligent chat',
        icon: 'Brain',
        route: '/admin/projects',
        color: '#13afe3',
        sortOrder: 1,
        isEnabled: true,
        isVisible: true,
        metadata: {
          category: 'core',
          gradient: ['#13afe3', '#6366f1'],
        },
      },
      {
        id: 'default_training',
        key: 'training',
        name: 'LLM Training',
        description: 'Fine-tune and train language models with custom datasets',
        icon: 'Learning',
        route: '/training/dashboard',
        color: '#059669',
        sortOrder: 2,
        isEnabled: true,
        isVisible: true,
        metadata: {
          category: 'tools',
          gradient: ['#059669', '#10b981'],
          badge: 'new',
        },
      },
      {
        id: 'default_demo',
        key: 'demo',
        name: 'Demo & Maximo',
        description: 'Interactive demonstrations and IBM Maximo work order management',
        icon: 'Presentation',
        route: '/demo/maximo/workorders',
        color: '#ec4899',
        sortOrder: 3,
        isEnabled: true,
        isVisible: true,
        metadata: {
          category: 'core',
          gradient: ['#ec4899', '#f472b6'],
        },
      },
      {
        id: 'default_iam',
        key: 'iam',
        name: 'IAM Service',
        description: 'Identity & Access Management - Users, API keys, Organizations',
        icon: 'Shield',
        route: '/iam',
        color: '#7c3aed',
        sortOrder: 4,
        isEnabled: true,
        isVisible: true,
        metadata: {
          category: 'admin',
          gradient: ['#7c3aed', '#a78bfa'],
        },
      },
      {
        id: 'default_finetuning',
        key: 'finetuning',
        name: 'Finetuning Studio',
        description: 'Manage training datasets, schemas, and GPU instances',
        icon: 'DatabaseExport',
        route: 'http://localhost:8002',
        color: '#f59e0b',
        sortOrder: 5,
        isEnabled: true,
        isVisible: true,
        metadata: {
          category: 'tools',
          gradient: ['#f59e0b', '#fbbf24'],
          badge: 'new',
          isExternal: true,
        },
      },
    ],
    categories: {
      core: { name: 'Core Applications', color: '#3b82f6' },
      tools: { name: 'Developer Tools', color: '#10b981' },
      admin: { name: 'Administration', color: '#8b5cf6' },
      experimental: { name: 'Experimental', color: '#f59e0b' },
    },
  };
}
